import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Upload } from 'antd';
import { PlusOutlined, CameraOutlined } from '@ant-design/icons';
import { Html5QrcodeScanner, Html5Qrcode } from "html5-qrcode";
import Tesseract from 'tesseract.js';
import { toast } from 'react-toastify';

const ISBNScanner = ({ isVisible, onClose, onISBNDetected }) => {
  const [scanner, setScanner] = useState(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // ISBN extraction functions
  const extractISBNFromText = (text) => {
    // Remove extra whitespace and normalize text
    const cleanText = text.replace(/\s+/g, ' ').trim();
    
    // More comprehensive ISBN patterns
    const isbnPatterns = [
      // ISBN-13 with various separators: 978-0-7334-2609-4, 978 0 7334 2609 4, 978073426094
      /(?:ISBN[- ]?)?(?:978|979)[- ]?[0-9]{1,5}[- ]?[0-9]{1,7}[- ]?[0-9]{1,6}[- ]?[0-9]/g,
      
      // ISBN-10 with various separators: 1-234567-56-8, 1 234567 56 8, 1234567568
      /(?:ISBN[- ]?)?[0-9]{1,5}[- ]?[0-9]{1,7}[- ]?[0-9]{1,6}[- ]?[0-9X]/g,
      
      // Simple numeric patterns (10-13 digits) - more flexible
      /\b(?:978|979)?[0-9]{9,12}[0-9X]?\b/g,
      
      // Pattern with spaces: 978 0 7334 2609 4
      /\b(?:978|979)[ ]?[0-9]{1,5}[ ]?[0-9]{1,7}[ ]?[0-9]{1,6}[ ]?[0-9X]\b/g,
      
      // Pattern without separators: 978073426094
      /\b(?:978|979)[0-9]{9,10}\b/g,
      
      // Generic 10-13 digit numbers that could be ISBNs
      /\b[0-9]{10,13}\b/g
    ];
    
    const foundISBNs = [];
    
    // Try each pattern
    isbnPatterns.forEach(pattern => {
      const matches = cleanText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          // Clean the match - remove all non-numeric characters except X
          const cleanedISBN = match.replace(/[^0-9X]/g, '');
          
          // Validate ISBN length and format
          if (cleanedISBN.length === 10 || cleanedISBN.length === 13) {
            // Additional validation for ISBN-13 (should start with 978 or 979)
            if (cleanedISBN.length === 13) {
              if (cleanedISBN.startsWith('978') || cleanedISBN.startsWith('979')) {
                foundISBNs.push({
                  original: match,
                  cleaned: cleanedISBN,
                  formatted: formatISBN(cleanedISBN)
                });
              }
            } else if (cleanedISBN.length === 10) {
              // ISBN-10 validation (last character can be X)
              if (/^[0-9]{9}[0-9X]$/.test(cleanedISBN)) {
                foundISBNs.push({
                  original: match,
                  cleaned: cleanedISBN,
                  formatted: formatISBN(cleanedISBN)
                });
              }
            }
          }
        });
      }
    });
    
    // Remove duplicates
    const uniqueISBNs = foundISBNs.filter((isbn, index, self) => 
      index === self.findIndex(t => t.cleaned === isbn.cleaned)
    );
    
    return uniqueISBNs;
  };

  const formatISBN = (isbn) => {
    const cleaned = isbn.replace(/[^0-9X]/g, '');
    
    if (cleaned.length === 13) {
      // ISBN-13: 978-0-7334-2609-4
      return `${cleaned.slice(0,3)}-${cleaned.slice(3,4)}-${cleaned.slice(4,8)}-${cleaned.slice(8,12)}-${cleaned.slice(12)}`;
    } else if (cleaned.length === 10) {
      // ISBN-10: 1-234567-56-8
      return `${cleaned.slice(0,1)}-${cleaned.slice(1,7)}-${cleaned.slice(7,9)}-${cleaned.slice(9)}`;
    }
    
    return cleaned;
  };

  const detectBarcodeInImage = async (imageFile) => {
    try {
      console.log("Attempting barcode detection in uploaded image...");
      
      const html5Qrcode = new Html5Qrcode("temp-reader");
      
      // Create a temporary div for barcode detection
      const tempDiv = document.createElement('div');
      tempDiv.id = 'temp-reader';
      tempDiv.style.display = 'none';
      document.body.appendChild(tempDiv);
      
      const result = await html5Qrcode.scanFile(imageFile, true);
      
      console.log("Barcode detected in image:", result);
      
      // Clean up
      document.body.removeChild(tempDiv);
      
      return result;
    } catch (error) {
      console.log("No barcode detected in image:", error.message);
      return null;
    }
  };

  const processImageWithOCR = async (imageFile) => {
    setIsProcessingImage(true);
    
    try {
      const { data: { text } } = await Tesseract.recognize(
        imageFile,
        'eng',
        {
          logger: m => console.log(m)
        }
      );
      
      console.log("OCR Text:", text);
      
      const foundISBNs = extractISBNFromText(text);
      
      if (foundISBNs.length > 0) {
        // Use the first found ISBN
        const selectedISBN = foundISBNs[0];
        
        // Call the callback with the cleaned ISBN
        onISBNDetected(selectedISBN.cleaned);
        
        toast.success(`ISBN extracted from image: ${selectedISBN.cleaned}`);
        
        // Close the modal
        onClose();
      } else {
        toast.error("No ISBN found in the image. Please try a clearer image or scan a barcode.");
      }
      
    } catch (error) {
      console.error("OCR Error:", error);
      toast.error("Error processing image. Please try again.");
    } finally {
      setIsProcessingImage(false);
    }
  };

  // Camera capture functions
  const startCameraCapture = async () => {
    try {
      setIsCapturing(true);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast.error("Unable to access camera. Please check permissions.");
      setIsCapturing(false);
    }
  };

  const stopCameraCapture = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCapturing(false);
    setCapturedImage(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'captured-photo.jpg', { type: 'image/jpeg' });
        setCapturedImage(file);
        stopCameraCapture();
        
        // Process the captured image
        processCapturedImage(file);
      }
    }, 'image/jpeg', 0.8);
  };

  const processCapturedImage = async (file) => {
    setIsProcessingImage(true);
    
    try {
      // First try to detect barcode in the captured image
      console.log("Trying barcode detection in captured image...");
      const barcodeResult = await detectBarcodeInImage(file);
      
      if (barcodeResult) {
        console.log("Barcode detected in captured image:", barcodeResult);
        
        // Clean up the ISBN (remove any non-numeric characters except X)
        const cleanISBN = barcodeResult.replace(/[^0-9X]/g, '');
        
        // Call the callback with the cleaned ISBN
        onISBNDetected(cleanISBN);
        
        toast.success(`ISBN detected from captured image: ${cleanISBN}`);
        onClose();
        return;
      }
      
      // If no barcode detected, try OCR
      console.log("No barcode found in captured image, trying OCR...");
      await processImageWithOCR(file);
      
    } catch (error) {
      console.error("Error processing captured image:", error);
      toast.error("Error processing captured image. Please try again.");
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleImageUpload = async (file) => {
    setIsProcessingImage(true);
    
    try {
      // First try to detect barcode in the image
      console.log("Trying barcode detection in uploaded image...");
      const barcodeResult = await detectBarcodeInImage(file);
      
      if (barcodeResult) {
        console.log("Barcode detected in image:", barcodeResult);
        
        // Clean up the ISBN (remove any non-numeric characters except X)
        const cleanISBN = barcodeResult.replace(/[^0-9X]/g, '');
        
        // Call the callback with the cleaned ISBN
        onISBNDetected(cleanISBN);
        
        toast.success(`ISBN detected from barcode image: ${cleanISBN}`);
        onClose();
        return false;
      }
      
      // If no barcode detected, try OCR
      console.log("No barcode found, trying OCR...");
      await processImageWithOCR(file);
      
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Error processing image. Please try again.");
    } finally {
      setIsProcessingImage(false);
    }
    
    return false; // Prevent default upload
  };

  const onScanSuccess = (decodedText, decodedResult) => {
    console.log("Barcode scan successful!");
    console.log("Raw decoded text:", decodedText);
    console.log("Decoded result:", decodedResult);
    
    // Extract ISBN from scanned result
    let isbn = decodedText;
    
    // Clean up the ISBN (remove any non-numeric characters except X)
    const cleanISBN = isbn.replace(/[^0-9X]/g, '');
    
    console.log("Cleaned ISBN:", cleanISBN);
    
    // Validate ISBN length
    if (cleanISBN.length < 10 || cleanISBN.length > 13) {
      console.log("Invalid ISBN length:", cleanISBN.length);
      toast.error(`Invalid ISBN length: ${cleanISBN.length} digits. Expected 10-13 digits.`);
      return;
    }
    
    // Call the callback with the cleaned ISBN
    onISBNDetected(cleanISBN);
    
    // Show success message
    toast.success(`ISBN scanned successfully: ${cleanISBN}`);
    
    // Close the modal
    onClose();
  };

  const onScanFailure = (error) => {
    // Handle scan failure - usually better to ignore and keep scanning
    console.log("Scan failed:", error);
  };

  const startScanner = () => {
    if (scanner) {
      console.log("Scanner already exists, skipping initialization");
      return;
    }
    
    console.log("Initializing barcode scanner...");
    setIsScanning(true);
    
    const newScanner = new Html5QrcodeScanner(
      "reader",
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      },
      false
    );
    
    newScanner.render(onScanSuccess, onScanFailure);
    setScanner(newScanner);
    console.log("Scanner initialized successfully");
  };

  const closeModal = () => {
    console.log("Closing scanner modal...");
    
    // Clean up scanner
    if (scanner) {
      try {
        scanner.clear();
        console.log("Scanner cleared successfully");
      } catch (error) {
        console.log("Error clearing scanner:", error);
      }
      setScanner(null);
    }
    
    // Clean up camera capture
    stopCameraCapture();
    
    setIsProcessingImage(false);
    setIsScanning(false);
    onClose();
  };

  // Start scanner when modal opens
  useEffect(() => {
    if (isVisible) {
      console.log("Modal is visible, starting scanner...");
      // Clear any existing scanner first
      if (scanner) {
        try {
          scanner.clear();
        } catch (error) {
          console.log("Error clearing existing scanner:", error);
        }
        setScanner(null);
      }
      
      // Start scanner after a short delay to ensure DOM is ready
      const timer = setTimeout(() => {
        startScanner();
      }, 200);
      
      return () => {
        clearTimeout(timer);
      };
    } else {
      // Clean up scanner when modal closes
      if (scanner) {
        try {
          scanner.clear();
        } catch (error) {
          console.log("Error clearing scanner on modal close:", error);
        }
        setScanner(null);
      }
      setIsScanning(false);
    }
  }, [isVisible]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (scanner) {
        try {
          scanner.clear();
          console.log("Scanner cleaned up on component unmount");
        } catch (error) {
          console.log("Error cleaning up scanner on unmount:", error);
        }
      }
      
      // Clean up camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        console.log("Camera stream cleaned up on component unmount");
      }
    };
  }, [scanner]);

  return (
    <>
      <style>
        {`
          @keyframes scanLine {
            0% { 
              top: 10%; 
              opacity: 0.3;
            }
            50% { 
              top: 50%; 
              opacity: 1;
            }
            100% { 
              top: 90%; 
              opacity: 0.3;
            }
          }
        `}
      </style>
      <Modal
        title="Scan ISBN Barcode or Upload Image"
        open={isVisible}
        onCancel={closeModal}
        footer={[
          <Button key="close" onClick={closeModal}>
            Close
          </Button>
        ]}
        width={600}
        centered
      >
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '20px' }}>
          <p style={{ marginBottom: '15px', color: '#666' }}>
            Choose one of the options below:
          </p>
          
          {/* Camera Capture Option */}
          {isCapturing && (
            <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #d9d9d9', borderRadius: '8px' }}>
              <h4 style={{ marginBottom: '10px' }}>📸 Take Photo</h4>
              <p style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>
                Take a photo of the book's ISBN or barcode
              </p>
              
              <div>
                <video 
                  ref={videoRef}
                  style={{ 
                    width: '100%', 
                    maxWidth: '300px', 
                    height: '200px',
                    border: '2px solid #d9d9d9',
                    borderRadius: '8px',
                    objectFit: 'cover'
                  }}
                  autoPlay
                  muted
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div style={{ marginTop: '10px' }}>
                  <Button 
                    type="primary" 
                    onClick={capturePhoto}
                    disabled={isProcessingImage}
                    style={{ marginRight: '10px' }}
                  >
                    Capture
                  </Button>
                  <Button onClick={stopCameraCapture}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Button Row - Take Photo and Upload Image */}
          {!isCapturing && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '15px', 
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}>
              <Button 
                type="primary" 
                icon={<CameraOutlined />}
                onClick={startCameraCapture}
                disabled={isProcessingImage}
                size="large"
              >
                Take Photo
              </Button>
              
              <Upload
                beforeUpload={handleImageUpload}
                accept="image/*"
                showUploadList={false}
                disabled={isProcessingImage}
              >
                <Button 
                  type="default" 
                  icon={<PlusOutlined />}
                  loading={isProcessingImage}
                  size="large"
                >
                  {isProcessingImage ? 'Processing...' : 'Upload Image'}
                </Button>
              </Upload>
            </div>
          )}
          
          {/* Divider */}
          <div style={{ margin: '20px 0', color: '#999' }}>OR</div>
          
          {/* Barcode Scanner Option */}
          <div style={{ padding: '15px', border: '1px solid #d9d9d9', borderRadius: '8px' }}>
            <h4 style={{ marginBottom: '10px' }}>📱 Scan Barcode</h4>
            <p style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>
              Use camera to scan ISBN barcode in real-time
            </p>
            <div style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '400px', margin: '0 auto' }}>
              <div 
                id="reader" 
                style={{ 
                  width: '100%', 
                  border: '2px solid #d9d9d9',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              ></div>
              
              {/* Scanning Animation Overlay */}
              {isScanning && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10,
                  pointerEvents: 'none'
                }}>
                  {/* Moving scanning line */}
                  <div style={{
                    position: 'absolute',
                    left: '10%',
                    right: '10%',
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, #1890ff, transparent)',
                    animation: 'scanLine 2s ease-in-out infinite',
                    boxShadow: '0 0 10px #1890ff'
                  }}></div>
                  
                  {/* Scanning text */}
                  {/* <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    Scanning...
                  </div> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
    </>
  );
};

export default ISBNScanner;
